import { useState, type FormEvent } from 'react'
import Section from '@/components/Section'
import NewsletterForm from '@/components/widgets/NewsletterForm'
import { usePageTitle } from '@/components/usePageTitle'
import { sendContact } from '@/lib/api'
import { validateContact, type ContactInput } from '@/lib/forms'

type Errors = Partial<Record<keyof ContactInput, string>>

function Field({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  autoComplete,
}: {
  label: string
  name: keyof ContactInput
  type?: string
  value: string
  onChange: (v: string) => void
  error?: string
  autoComplete?: string
}) {
  return (
    <label className="block">
      <span className="text-[1.05em]">
        {label} <span className="opacity-70">(required)</span>
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete ?? 'off'}
        aria-invalid={error ? true : undefined}
        className="field mt-4"
      />
      <span className="mt-2 block min-h-6 text-sm">{error}</span>
    </label>
  )
}

const EMPTY: ContactInput = { firstName: '', lastName: '', email: '', subject: '', message: '' }

/** Contact page. Messages are saved to the `contact_messages` table through /api/contact. */
export default function Contact() {
  usePageTitle('Contact', 'Get in touch with the International ColorMark Organization.')
  const [form, setForm] = useState<ContactInput>(EMPTY)
  const [website, setWebsite] = useState('')
  const [errors, setErrors] = useState<Errors>({})
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [failure, setFailure] = useState<string | null>(null)
  const set = (k: keyof ContactInput) => (v: string) => setForm((f) => ({ ...f, [k]: v }))

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFailure(null)
    const v = validateContact({ ...form })
    if (!v.ok) {
      setErrors(v.errors)
      return
    }
    setErrors({})
    setState('sending')
    try {
      await sendContact({ ...v.value, website })
      setState('sent')
    } catch {
      setState('idle')
      setFailure('Sorry, your message did not go through. Please try again, or email contact@colormark.org.')
    }
  }

  return (
    <>
      <Section image="/images/contact-rainbow-blur.webp" overlay={0.15} tone="light" className="pt-40 md:pt-56">
        <div className="mx-auto grid max-w-[1800px] grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <h2>Get in touch.</h2>
            <p className="mt-8">
              Whether you have a question about registration, a comment about our products, or anything else, we would
              love to hear from you!
            </p>
            <p className="mt-6">Please use the contact form to send us a message.</p>
            <h4 className="mt-12 font-medium">
              <a href="mailto:contact@colormark.org">contact@colormark.org</a>
            </h4>
          </div>

          <div>
            {state === 'sent' ? (
              <div className="pt-6" role="status">
                <h3>Thank you!</h3>
                <p className="mt-6">Your message has been received. We will get back to you at {form.email}.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6 pt-6" noValidate>
                <fieldset className="space-y-4">
                  <legend className="mb-4 text-[1.15em]">Name</legend>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field label="First Name" name="firstName" value={form.firstName} onChange={set('firstName')} error={errors.firstName} autoComplete="given-name" />
                    <Field label="Last Name" name="lastName" value={form.lastName} onChange={set('lastName')} error={errors.lastName} autoComplete="family-name" />
                  </div>
                </fieldset>
                <Field label="Email" name="email" type="email" value={form.email} onChange={set('email')} error={errors.email} autoComplete="email" />
                <Field label="Subject" name="subject" value={form.subject} onChange={set('subject')} error={errors.subject} />
                <label className="block">
                  <span className="text-[1.05em]">
                    Message <span className="opacity-70">(required)</span>
                  </span>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={(e) => set('message')(e.target.value)}
                    required
                    rows={5}
                    aria-invalid={errors.message ? true : undefined}
                    className="field mt-4 resize-y"
                  />
                  <span className="mt-2 block min-h-6 text-sm">{errors.message}</span>
                </label>
                {/* Honeypot: hidden from people, filled by bots. */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-px w-px opacity-0"
                />
                <button type="submit" className="btn btn-white" disabled={state === 'sending'}>
                  {state === 'sending' ? 'Sending…' : 'Submit'}
                </button>
                <p className="min-h-6 text-sm" role="alert">
                  {failure}
                </p>
              </form>
            )}
          </div>
        </div>
      </Section>

      <Section>
        <NewsletterForm tone="dark" source="contact" />
      </Section>
    </>
  )
}
